import { useAtom } from 'jotai';
import { notificationState, socialState } from '@/recoil/atom';
import { useNotiTokens, useNotiTopics, useSetToken } from '@/hooks/apiHooks';
import { useEffect, useRef } from 'react';
import {
  getMessaging,
  getToken,
  MessagePayload,
  onMessage,
} from '@firebase/messaging';
import firebaseApp, { requestPermission } from '@/utils/firebase/initialize';
import process from 'process';
import { getCookie, setCookie } from '@/utils/tokens';
import { COOKIE_KEY_FIREBASE_TOKEN } from '@/constant/keys';
import { NotificationData } from '@/types/api';
import { fetchApi } from '@/utils/api/customAxios';
import { getFormattedNow } from '@/utils/format';

const listSize = 50;
const TOPIC_NAME = 'refresh_social';

export const useNotificationTopics = () => {
  const [, setState] = useAtom(socialState);
  const [, setNotifications] = useAtom(notificationState);
  const { data: topicsData } = useNotiTopics(listSize);
  const { data: tokensData } = useNotiTokens(listSize);

  // 이미 구독 처리가 되었는지 여부를 저장하는 ref
  const subscribedRef = useRef(false);

  // 전역 메시지 큐 (메모리 상에 저장)
  const pendingMessages = useRef<MessagePayload[]>([]);

  useEffect(() => {
    // 문서의 가시성 변화 이벤트를 감지
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        processPendingMessages();
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'FIREBASE_BACKGROUND_MESSAGING') {
        pendingMessages.current.push(event.data.payload);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 안전한 체크
    if (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      navigator.serviceWorker
    ) {
      //서비스 워커에서 백그라운드 이벤트 전달 수신
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    // cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then(() => {
          return requestPermission();
        })
        .then(() => {
          const messaging = getMessaging(firebaseApp);

          getToken(messaging, {
            vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
          })
            .then((token) => {
              useSetToken(token).then(() =>
                setCookie(COOKIE_KEY_FIREBASE_TOKEN, token),
              );
            })
            .catch((e) => console.error(e));

          onMessage(messaging, (payload) => {
            if (payload.data?.refreshSection === 'social') {
              if (document.visibilityState === 'visible') {
                boardApiRefresh(payload);
              } else {
                pendingMessages.current.push(payload);
              }
            } else {
              const newNotification: NotificationData = {
                title: payload.notification?.title || '',
                body: payload.notification?.body || '',
                link: payload.fcmOptions?.link || '',
                messageId: payload.messageId,
              };

              setNotifications((prev) => [newNotification, ...prev]);
            }
          });
        });
    }
  }, []);

  useEffect(() => {
    // FIREBASE_TOKEN이 없거나, 이미 구독을 진행했다면 더 이상 실행하지 않음
    if (!getCookie(COOKIE_KEY_FIREBASE_TOKEN) || subscribedRef.current) return;

    // topicsData와 tokensData가 존재하고 데이터가 있을 때만 실행
    if (topicsData?.length && tokensData?.length) {
      // FIREBASE_TOKEN 쿠키와 일치하는 토큰 객체 찾기
      const tokenObj = tokensData.find(
        (tokenItem) => getCookie(COOKIE_KEY_FIREBASE_TOKEN) === tokenItem.token,
      );
      if (!tokenObj?.id) {
        console.warn('No matching browser token found.');
        return;
      }

      const subscribeToTopics = async () => {
        try {
          await Promise.all(
            topicsData.map((topic) => {
              if (topic.topic === TOPIC_NAME) {
                return fetchApi({
                  method: 'POST',
                  url: '/api/v1/notificationCenter/subscribe/topic',
                  baseURL: process.env.NEXT_PUBLIC_NOTIFICATION_URL,
                  data: {
                    tokenId: tokenObj.id,
                    topicId: topic.id,
                  },
                });
              }
              // 조건에 맞지 않으면 resolved Promise 반환
              return Promise.resolve();
            }),
          );
          // 구독 작업이 완료되었으므로 플래그 업데이트
          subscribedRef.current = true;
          console.log('Subscription complete.');
        } catch (error) {
          console.error('Error during subscription:', error);
        }
      };

      subscribeToTopics();
    }
  }, [topicsData, tokensData]);

  // 큐에 저장된 메시지를 처리하는 함수
  const processPendingMessages = () => {
    while (pendingMessages.current.length > 0) {
      const payload = pendingMessages.current.shift();
      if (payload) {
        boardApiRefresh(payload); // 저장된 메시지를 이용하여 API 새로고침 실행
        pendingMessages.current.length = 0; // 리프래쉬 푸쉬는 백그라운드 상태에서 최대 1회만 실행되도록
      }
    }
  };

  const boardApiRefresh = (payload: MessagePayload) => {
    if (payload.data?.refreshSection === 'social') {
      // 5초 ~ 60초 사이의 랜덤 딜레이 (밀리초 단위)
      const randomDelay = Math.floor(Math.random() * (15000 - 3000 + 1)) + 3000; //3초~15초
      console.log(`Refreshing board API after ${randomDelay / 1000} 초 delay`);
      setTimeout(() => {
        setState((prevState) => ({
          ...prevState,
          currentDate: getFormattedNow(),
          isTopicRender: true,
        }));
      }, randomDelay);
    }
  };
};

// reservedAt(YYYY-MM-DD HH:mm)이 있으면 그 시각에 예약 발송한다.
// 예약을 취소할 수 있게 jobMessage id 를 게시글에 저장할 곳이 없어 취소는 하지 않는다.
// 남은 예약이 발송돼도 목록을 한 번 더 새로고침할 뿐이다.
export function boardWebPushCall(reservedAt?: string) {
  fetchApi({
    method: 'POST',
    url: '/api/v1/webPush/topic/sendByTemplate',
    baseURL: process.env.NEXT_PUBLIC_NOTIFICATION_URL,
    data: {
      templateKey: 'topic_refresh',
      templateData: {
        data: {
          refreshSection: 'social',
        },
      },
      topic: TOPIC_NAME,
      sendBy: 'queue',
      reservedAt,
    },
  });
}
